import { useQuery, useMutation } from "@tanstack/react-query";
import { requestApi } from "@/shared/utills/http";
import { IAvailableReviewPeriod, IPeriodSummary, IPeriodSkills, IPeriodValueDelivered } from "./model";

const ENDPOINTS = {
  AVAILABLE_PERIODS: "/employee/prh/periods",
  PERIOD_SUMMARY: (periodId: number) => `/employee/prh/summary/${periodId}`,
  PERIOD_SKILLS: (periodId: number) => `/employee/prh/skills/${periodId}`,
  PERIOD_VALUE_DELIVERED: (periodId: number) => `/employee/prh/value/${periodId}`,
  DOWNLOAD_MEMO: (periodId: number) => `/employee/prh/memo/${periodId}`,
  DOWNLOAD_REPORT: (periodId: number) => `/employee/prh/export/${periodId}`,
};

export const REVIEW_PERIOD_QUERY_KEYS = {
  AVAILABLE_PERIODS: "availableReviewPeriods",
  PERIOD_SUMMARY: "periodSummary",
  PERIOD_SKILLS: "periodSkills",
  PERIOD_VALUE_DELIVERED: "periodValueDelivered",
};

export const useGetAvailableReviewPeriods = () => {
  return useQuery({
    queryKey: [REVIEW_PERIOD_QUERY_KEYS.AVAILABLE_PERIODS],
    queryFn: async () => {
      const data = await requestApi<IAvailableReviewPeriod[]>({
        path: ENDPOINTS.AVAILABLE_PERIODS,
      });
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetPeriodSummary = (periodId: number | undefined) => {
  return useQuery({
    queryKey: [REVIEW_PERIOD_QUERY_KEYS.PERIOD_SUMMARY, periodId],
    queryFn: async () => {
      const data = await requestApi<IPeriodSummary>({
        path: ENDPOINTS.PERIOD_SUMMARY(periodId!),
      });
      return data;
    },
    enabled: !!periodId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetPeriodSkills = (periodId: number | undefined) => {
  return useQuery({
    queryKey: [REVIEW_PERIOD_QUERY_KEYS.PERIOD_SKILLS, periodId],
    queryFn: async () => {
      const data = await requestApi<IPeriodSkills>({
        path: ENDPOINTS.PERIOD_SKILLS(periodId!),
      });
      return data;
    },
    enabled: !!periodId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetPeriodValueDelivered = (periodId: number | undefined) => {
  return useQuery({
    queryKey: [REVIEW_PERIOD_QUERY_KEYS.PERIOD_VALUE_DELIVERED, periodId],
    queryFn: async () => {
      const data = await requestApi<IPeriodValueDelivered>({
        path: ENDPOINTS.PERIOD_VALUE_DELIVERED(periodId!),
      });
      return data.value_delivered;
    },
    enabled: !!periodId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useDownloadPeriodMemo = () => {
  return useMutation({
    mutationFn: async (periodId: number) => {
      const data = await requestApi<string>({
        path: ENDPOINTS.DOWNLOAD_MEMO(periodId),
      });
      return data;
    },
    onSuccess: (response) => {
      const link = document.createElement("a");
      link.target = "_blank";
      link.href = response;
      link.download = "";
      link.click();
    },
  });
};

export const useDownloadPeriodReport = () => {
  return useMutation({
    mutationFn: async (periodId: number) => {
      const data = await requestApi<string>({
        path: ENDPOINTS.DOWNLOAD_REPORT(periodId),
      });
      return data;
    },
    onSuccess: (response) => {
      const link = document.createElement("a");
      link.target = "_blank";
      link.href = response;
      link.download = "";
      link.click();
    },
  });
};
